import { baseApi } from "../baseApi";
import { pick } from "../util";
import type {
  ConvertedFileResult,
  EditedImageResult,
  ImageEditInput,
  RequestUploadUrlInput,
  UploadUrlResult,
} from "../types";

export const uploadApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    requestUploadUrl: builder.mutation<UploadUrlResult, RequestUploadUrlInput>({
      query: (input) => ({
        body: {
          query: `
            mutation RequestUploadUrl(
              $entityType: UploadEntityType!
              $entityId: String!
              $fileName: String!
              $mimeType: String!
              $fileSize: Int!
            ) {
              requestUploadUrl(
                entityType: $entityType
                entityId: $entityId
                fileName: $fileName
                mimeType: $mimeType
                fileSize: $fileSize
              ) {
                uploadUrl
                key
              }
            }
          `,
          variables: input,
        },
      }),

      transformResponse: (res: unknown) => pick<UploadUrlResult>(res, "requestUploadUrl"),
    }),

    // For file types that can't be used as-uploaded: video -> HLS, HEIC/HEIF
    // -> PNG. Call once the raw PUT to `uploadUrl` succeeds, then store the
    // returned key in place of the originally-uploaded one.
    convertUploadedFile: builder.mutation<ConvertedFileResult, { key: string; mimeType: string }>({
      query: (input) => ({
        body: {
          query: `
            mutation ConvertUploadedFile($key: String!, $mimeType: String!) {
              convertUploadedFile(key: $key, mimeType: $mimeType) {
                key
              }
            }
          `,
          variables: input,
        },
      }),

      transformResponse: (res: unknown) => pick<ConvertedFileResult>(res, "convertUploadedFile"),
    }),

    // Crop / rotate / colour-grade an image already in S3. Renders server-side
    // and returns a *new* key — the source is never overwritten, so anything
    // still pointing at it keeps working. See utils/imageEdit.ts for how the
    // edit is built and why the preview matches what comes back.
    editImage: builder.mutation<
      EditedImageResult,
      { key: string; fileName: string; edit: ImageEditInput }
    >({
      query: (input) => ({
        body: {
          query: `
            mutation EditImage($key: String!, $fileName: String!, $edit: ImageEditInput!) {
              editImage(key: $key, fileName: $fileName, edit: $edit) {
                key
                fileName
                mimeType
                fileSize
              }
            }
          `,
          variables: input,
        },
      }),

      transformResponse: (res: unknown) => pick<EditedImageResult>(res, "editImage"),
    }),
  }),
});

export const {
  useRequestUploadUrlMutation,
  useConvertUploadedFileMutation,
  useEditImageMutation,
} = uploadApi;
